import { Box, Button, FormControl, FormLabel, Input, Stack, Textarea, Typography } from '@mui/joy';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SaveIcon from '@/assets/icons/save.svg?react';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { BaseHeader } from '@/components/navigations/BaseHeader';
import { DraftView } from '@/components/processes/DraftView';
import { I18N_DE } from '@/i18n/de';
import { UserGuard } from '@/modules/UserGuard';
import { ExaminationStatus, UpdateProcessExaminationDto } from '@/modules/api';
import {
  backendApiProcessesFindOneExaminationPublicOptions,
  backendApiProcessesUpdateExaminationPublicMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { useUserQuery } from '@/queries/useUserQuery';

export const Route = createFileRoute('/_authenticated/pruefung/$processId/$examinationId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { processId, examinationId } = useParams({ strict: false });
  const [success, setSuccess] = useState(false);

  const { data: examination, error } = useQuery({
    ...backendApiProcessesFindOneExaminationPublicOptions({
      path: { processId: processId!, examinationId: examinationId! },
    }),
    enabled: !!processId && !!examinationId,
  });

  const {
    register,
    reset,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Pick<UpdateProcessExaminationDto, 'contactName' | 'value' | 'status'>>();

  useEffect(() => {
    if (!examination) return;
    reset({
      contactName: examination.contactName,
      value: examination.value,
      status: examination.status,
    });
  }, [examination, reset]);

  const { mutate, isPending } = useMutation({
    ...backendApiProcessesUpdateExaminationPublicMutation(),
    onSuccess: () => setSuccess(true),
  });

  const { data: user } = useUserQuery();

  return (
    <>
      {UserGuard.isSingleSignOnUser(user) && (
        <Box sx={{ width: '100%' }}>
          <BaseHeader />
        </Box>
      )}
      <Stack gap={2} maxWidth={800} p={2}>
        {error && (
          <>
            <Typography level="h2">Anfrage fehlgeschlagen</Typography>
            <Typography>
              Die Anfrage ist ungültig. Bitte wenden Sie sich an die Person, von der Sie diesen Link erhalten haben.
            </Typography>
          </>
        )}

        {!error && !success && (
          <Stack
            gap={2}
            component="form"
            onSubmit={handleSubmit((data) => {
              if (isPending) return;

              mutate({ path: { processId: processId!, examinationId: examinationId! }, body: data });
            })}
          >
            <Typography level="h2">{`Prüfung zur verkehrsrechtlichen Anordnung ${examination?.process.sequentialId}`}</Typography>
            <Typography>
              Sie haben diesen Link erhalten um für die Ihnen übermittelte verkehrsrechtliche Anordnung Stellung zu
              nehmen. Bitte füllen Sie die Felder Name, Stellungnahme, Status aus und bestätigen diese dann mit der
              Schaltfläche Speichern.
            </Typography>

            {examination && <DraftView process={examination.process} />}

            <FormControl>
              <FormLabel>Organisation</FormLabel>
              <Input type="text" value={examination?.contactOrganization} sx={{ width: '300px' }} readOnly />
            </FormControl>

            <FormControl error={!!errors.contactName}>
              <FormLabel>Name</FormLabel>
              <Input
                type="text"
                variant="outlined"
                sx={{ width: '300px' }}
                slotProps={{
                  input: {
                    ...register('contactName'),
                  },
                }}
              />
            </FormControl>

            <FormControl error={!!errors.value}>
              <FormLabel>Stellungnahme</FormLabel>
              <Textarea
                variant="outlined"
                minRows={5}
                slotProps={{ textarea: { ...register('value') } }}
                sx={{ p: 1 }}
              />
            </FormControl>
            <FormControl error={!!errors.status}>
              <FormLabel>Status</FormLabel>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    size="sm"
                    options={Object.values(ExaminationStatus).filter(
                      (examinationStatus) => examinationStatus !== 'CANCELLED',
                    )}
                    optionLabel={({ option }) => I18N_DE.ExaminationStatus[option]}
                  />
                )}
              />
            </FormControl>

            <Stack direction="row" gap={2}>
              <Button
                startDecorator={<SaveIcon />}
                type="submit"
                disabled={!watch('contactName') || !watch('value') || isPending}
                loading={isPending}
              >
                Speichern
              </Button>
            </Stack>
          </Stack>
        )}
        {!error && success && (
          <>
            <Typography>Vielen Dank für Ihre Eingabe</Typography>
            <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => setSuccess(false)}>
              Eingabe bearbeiten
            </Button>
          </>
        )}
      </Stack>
    </>
  );
}
